/**
 * Shared modal overlay primitives used by shell dialogs and audit export gate.
 */

import { ANIM_MS } from '../shared/timing.js';
import { prefersReducedMotion } from './feedback.js';
import { trapFocus, focusInitialIn, isAnyModalOpen, isTopFocusTrap } from './focus-trap.js';

let modalStackObserver = null;
let modalStackSyncQueued = false;

function getModalBaseZ() {
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--pb-z-modal').trim();
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : 300;
}

export function queueModalStackSync() {
  if (modalStackSyncQueued) return;
  modalStackSyncQueued = true;
  requestAnimationFrame(() => {
    modalStackSyncQueued = false;
    syncModalStack();
  });
}

export function syncModalStack() {
  const overlays = Array.from(document.body?.querySelectorAll('.pb-modal-overlay') || []).filter(
    (overlay) => overlay.isConnected && !overlay._pbClosing
  );
  const baseZ = getModalBaseZ();

  overlays.forEach((overlay, idx) => {
    const isTop = idx === overlays.length - 1;
    overlay.style.zIndex = String(baseZ + idx);

    if (isTop) {
      overlay.removeAttribute('aria-hidden');
      overlay.style.pointerEvents = '';
      if ('inert' in overlay) overlay.inert = false;
      return;
    }

    overlay.setAttribute('aria-hidden', 'true');
    overlay.style.pointerEvents = 'none';
    if ('inert' in overlay) overlay.inert = true;
  });

  // Non-modal sheets (Mobile P13) are deliberately NOT stack members — they
  // have no overlay, which is exactly what leaves the canvas above them live
  // and hit-testable (D1). But "not a stack member" must not mean "still in the
  // a11y tree behind a modal": a screen reader would happily walk into a sheet
  // the user cannot see past, and Tab would land there too.
  //
  // Four lines, here rather than in sheet.js, because this is the one function
  // that already knows how many modals are open.
  const modalOpen = overlays.length > 0;
  for (const sheet of document.body?.querySelectorAll('.pb-sheet--nonmodal') || []) {
    if ('inert' in sheet) sheet.inert = modalOpen;
    if (modalOpen) sheet.setAttribute('aria-hidden', 'true');
    else sheet.removeAttribute('aria-hidden');
  }
}

export function initModalStacking() {
  if (modalStackObserver || !document.body) return;
  modalStackObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType === Node.ELEMENT_NODE && node.classList?.contains('pb-modal-overlay')) {
          queueModalStackSync();
          return;
        }
      }
      for (const node of mutation.removedNodes) {
        if (node.nodeType === Node.ELEMENT_NODE && node.classList?.contains('pb-modal-overlay')) {
          queueModalStackSync();
          return;
        }
      }
    }
  });
  modalStackObserver.observe(document.body, { childList: true });
  syncModalStack();
}

export function removeModalOverlay(overlay) {
  if (!overlay || overlay._pbClosing) return;
  if (prefersReducedMotion()) {
    overlay.remove();
    queueModalStackSync();
    return;
  }
  overlay._pbClosing = true;
  overlay.style.pointerEvents = 'none';
  overlay.classList.add('pb-modal-overlay-closing');
  queueModalStackSync();

  const done = () => {
    overlay.removeEventListener('animationend', done);
    if (overlay.isConnected) overlay.remove();
    queueModalStackSync();
  };

  overlay.addEventListener('animationend', done, { once: true });
  setTimeout(done, ANIM_MS + 20);
}

export { trapFocus, focusInitialIn, isAnyModalOpen, isTopFocusTrap };
